/**
 * Admin API - all admin-related requests. Uses shared api instance and central endpoints.
 */
import api from '../utils/api';
import { ENDPOINTS } from '../config/endpoints';

const { ADMIN } = ENDPOINTS;

// Dashboard
export const getDashboardStats = () => api.get(ADMIN.DASHBOARD_STATS);
export const getAdminContext = () => api.get(ADMIN.CONTEXT);

// Salons
export const getSalons = (params = {}) => api.get(ADMIN.SALONS, { params });
export const getSalonById = (id) => api.get(ADMIN.salonById(id));
export const createSalon = (data) => api.post(ADMIN.SALONS, data);
export const updateSalon = (id, data) => api.put(ADMIN.salonById(id), data);
export const deleteSalon = (id) => api.delete(ADMIN.salonById(id));
export const mapServicesToSalon = (salonId, serviceIds) =>
  api.post(ADMIN.salonServices(salonId), { serviceIds });
export const getBranchAdmin = (id) => api.get(ADMIN.salonAdmin(id));
export const upsertBranchAdmin = (id, data) => api.put(ADMIN.salonAdmin(id), data);

// Mirrors
export const getMirrors = (params = {}) => api.get(ADMIN.MIRRORS, { params });
export const getNextMirrorId = (salonId) =>
  api.get(ADMIN.MIRRORS_NEXT_ID, { params: salonId ? { salonId } : {} });
export const getNextMirrorCode = ({ brandCode, branchCode } = {}) =>
  api.get(ADMIN.MIRRORS_NEXT_CODE, { params: { brandCode, branchCode } });
export const getMirrorById = (id) => api.get(ADMIN.mirrorById(id));
export const createMirror = (data) => api.post(ADMIN.MIRRORS, data);
export const updateMirror = (id, data) => api.put(ADMIN.mirrorById(id), data);
export const deleteMirror = (id) => api.delete(ADMIN.mirrorById(id));
export const mapServicesToMirror = (mirrorId, serviceIds) =>
  api.post(ADMIN.mirrorServices(mirrorId), { serviceIds });
export const mapFeaturePacksToMirror = (mirrorId, featurePackIds) =>
  api.post(ADMIN.mirrorFeaturePacks(mirrorId), { featurePackIds });

// Categories (admin list - all including inactive)
export const getAdminCategories = (params = {}) => api.get(ADMIN.CATEGORIES, { params });

// Stylists
export const getStylists = (params = {}) => api.get(ADMIN.STYLISTS, { params });
export const getStylistById = (id) => api.get(ADMIN.stylistById(id));
export const createStylist = (data) => api.post(ADMIN.STYLISTS, data);
export const updateStylist = (id, data) => api.put(ADMIN.stylistById(id), data);
export const deleteStylist = (id) => api.delete(ADMIN.stylistById(id));

// Services (admin list + bulk upload)
export const getAdminServices = (params = {}) => api.get(ADMIN.SERVICES, { params });
export const bulkUploadServices = (formData) =>
  api.post(ADMIN.SERVICES_BULK_UPLOAD, formData);
export const bulkAssignBrandToServices = (data) =>
  api.post(ADMIN.SERVICES_BULK_ASSIGN_BRAND, data);
export const uploadAdminMedia = (formData, onUploadProgress) =>
  api.post(ADMIN.MEDIA_UPLOAD, formData, { onUploadProgress });

// Feature packs
export const getFeaturePacks = (params = {}) => api.get(ADMIN.FEATURE_PACKS, { params });
export const getFeaturePackById = (id) => api.get(ADMIN.featurePackById(id));
export const createFeaturePack = (data) => api.post(ADMIN.FEATURE_PACKS, data);
export const updateFeaturePack = (id, data) => api.put(ADMIN.featurePackById(id), data);
export const deleteFeaturePack = (id) => api.delete(ADMIN.featurePackById(id));

// Users, transactions, reports
export const getUsers = (params = {}) => api.get(ADMIN.USERS, { params });
export const getUserById = (id) => api.get(`${ADMIN.USERS}/${id}`);
export const getTransactions = (params = {}) => api.get(ADMIN.TRANSACTIONS, { params });
export const getOngoingServices = (params = {}) => api.get(ADMIN.ONGOING_SERVICES, { params });
export const cancelOngoingBooking = (bookingId) => api.post(ADMIN.ongoingServiceCancel(bookingId));
export const getConsultationReport = (params = {}) =>
  api.get(ADMIN.REPORTS_CONSULTATIONS, { params });
export const getFeedbacks = (params = {}) => api.get(ADMIN.FEEDBACK, { params });
export const updateFeedbackStatus = (feedbackId, status) =>
  api.put(ADMIN.feedbackStatus(feedbackId), { status });

// Transaction Image Management
export const getTransactionById = (transactionId) => api.get(`/admin/transactions/${transactionId}`);
export const addTransactionImages = (transactionId, images) =>
  api.post(`/admin/transactions/${transactionId}/images`, { images });
export const removeTransactionImage = (transactionId, imageIndex) =>
  api.delete(`/admin/transactions/${transactionId}/images/${imageIndex}`);

// Approvals
export const getPendingApprovals = () => api.get(ADMIN.BOOKINGS_PENDING_APPROVAL);
export const approveBooking = (bookingId, action) =>
  api.post(ADMIN.bookingApprove(bookingId), { action });

// Brands
export const getBrands = (params = {}) => api.get(ADMIN.BRANDS, { params });
export const getActiveBrands = () => api.get(ADMIN.BRANDS_ACTIVE);
export const getBrandById = (id) => api.get(ADMIN.brandById(id));
export const createBrand = (data) => api.post(ADMIN.BRANDS, data);
export const updateBrand = (id, data) => api.put(ADMIN.brandById(id), data);
export const deleteBrand = (id) => api.delete(ADMIN.brandById(id));
export const getBrandStats = (id) => api.get(ADMIN.brandStats(id));
export const getBrandAdmin = (id) => api.get(ADMIN.brandAdmin(id));
export const upsertBrandAdmin = (id, data) => api.put(ADMIN.brandAdmin(id), data);

// Feeds
export const getFeeds = (params = {}) => api.get(ADMIN.FEEDS, { params });
export const createFeed = (data) => api.post(ADMIN.FEEDS, data);
export const updateFeed = (id, data) => api.put(ADMIN.feedById(id), data);
export const deleteFeed = (id) => api.delete(ADMIN.feedById(id));
export const duplicateFeed = (id) => api.post(ADMIN.feedDuplicate(id));

// Promotional feeds (for TV confirmation ads)
export const getPromotionalFeeds = (params = {}) => api.get(ADMIN.PROMOTIONAL_FEEDS, { params });
export const createPromotionalFeed = (data) => api.post(ADMIN.PROMOTIONAL_FEEDS, data);
export const updatePromotionalFeed = (id, data) => api.put(ADMIN.promotionalFeedById(id), data);
export const deletePromotionalFeed = (id) => api.delete(ADMIN.promotionalFeedById(id));
export const duplicatePromotionalFeed = (id) => api.post(ADMIN.promotionalFeedDuplicate(id));


// Engagement services
export const getEngagementServices = (params = {}) => api.get(ADMIN.ENGAGEMENT_SERVICES, { params });
export const createEngagementService = (data) => api.post(ADMIN.ENGAGEMENT_SERVICES, data);
export const updateEngagementService = (id, data) => api.put(ADMIN.engagementServiceById(id), data);
export const deleteEngagementService = (id) => api.delete(ADMIN.engagementServiceById(id));
export const duplicateEngagementService = (id) => api.post(ADMIN.engagementServiceDuplicate(id));
